import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faPhone,
  faCalendarPlus,
  faCircle,
  faEdit,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";

const EditSellerModal = ({ seller, onUpdate, onClose }) => {
  const [formData, setFormData] = useState({
    seller_name: "",
    phone: "",
    added_date: "",
    status: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (seller) {
      setFormData({
        seller_name: seller.seller_name || "",
        phone: seller.phone || "",
        added_date: seller.added_date || "",
        status: seller.status || "Active",
      });
    }
  }, [seller]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\s/g, "");
    const tzPattern = /^\+255[0-9]{9}$/;
    const localPattern = /^[0-9]{9,10}$/;
    return tzPattern.test(cleanPhone) || localPattern.test(cleanPhone);
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!formData.seller_name.trim()) {
      newErrors.seller_name = "Seller name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhoneNumber(formData.phone)) {
      newErrors.phone =
        "Please enter a valid phone number (e.g., +255777123456 or 0777123456)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate({
      ...seller,
      ...formData,
    });
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Seller"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6"
          >
            <FontAwesomeIcon icon={faSave} />
            Update Seller
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Info Banner */}
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold">Edit seller</p>
            <p className="text-xs text-gray-600">Update seller details</p>
          </div>
        </div>

        {/* Username - Read Only */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUser}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Username
          </label>
          <div className="flex h-10 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-600 items-center">
            {seller?.username || "N/A"}
          </div>
          <p className="text-xs text-gray-500">Username cannot be changed</p>
        </div>

        {/* Seller Name */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUser}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Seller Name *
          </label>
          <Input
            name="seller_name"
            value={formData.seller_name}
            onChange={handleChange}
            placeholder="Enter seller name"
            className={`border ${
              errors.seller_name ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.seller_name && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.seller_name}
            </p>
          )}
        </div>

        {/* Email - Read Only */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faEnvelope}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Email
          </label>
          <div className="flex h-10 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-600 items-center">
            {seller?.email || "N/A"}
          </div>
          <p className="text-xs text-gray-500">Email cannot be changed</p>
        </div>

        {/* Phone Number */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faPhone}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Phone Number *
          </label>
          <Input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter phone number (+255777123456)"
            className={`border ${
              errors.phone ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.phone && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.phone}
            </p>
          )}
        </div>

        {/* Added Date */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarPlus}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Added Date
          </label>
          <Input
            name="added_date"
            type="date"
            value={formData.added_date}
            onChange={handleChange}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30"
          />
        </div>

        {/* Status */}
        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCircle}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Status
          </label>
          <select
            name="status"
            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-800/30"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>
    </Modal>
  );
};

export default EditSellerModal;
